import { Descriptions, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import { getCampusCoupons, getStudentCoupons } from '../../../api/coupons.api';
import ErrorResponse from '../../../components/ErrorResponse';
import Loader from '../../../components/Spinner';
import useAxios from '../../../hooks/useAxios';
import {
  ICampusCoupon,
  IGetCampusCoupons,
  IGetStudentCoupons,
  IStudentCoupon,
} from '../../../models/coupons.model';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Footer from '../../../components/layout/Footer';
import Pagination from '../../../components/Pagination';
import config from '../../../lib/config';
import Button from '../../../components/Button';
import { getCommonCodeLabel } from '../../../lib/store';

interface IMatch {
  campusCode: string;
  couponCode: string;
}

const PaymentCouponList: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const match = useRouteMatch<IMatch>();
  const campusCode = match.params.campusCode;
  const couponCode = Number(match.params.couponCode);
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetCampusCoupons>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetStudentCoupons>();

  const [campusCoupon, setCampusCoupon] = useState<ICampusCoupon>();
  const [data, setData] = useState<IStudentCoupon[]>([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    execute(getCampusCoupons(campusCode));
    execute2(getStudentCoupons(campusCode));
  }, []);

  useEffect(() => {
    if (res && !isLoading) {
      const coupon = res.data.find((item) => item.couponCode === couponCode);
      if (coupon) setCampusCoupon(coupon);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (res2 && !isLoading2) {
      let _data = res2.data.filter((item) => item.couponCode === couponCode);
      setData(_data);
    }
  }, [res2, isLoading2]);

  useEffect(() => {
    if (query.page) {
      setPage(Number(query.page));
    }
  }, [location]);

  const columns: TableColumnsType<any> = [
    {
      title: t('payment.coupon.column1'),
      dataIndex: 'memberName',
      key: 'memberName',
      align: 'center',
      sorter: (a, b) => a.memberName?.localeCompare(b.memberName),
    },
    {
      title: t('payment.coupon.column7'),
      dataIndex: 'schoolName',
      key: 'schoolName',
      align: 'center',
      sorter: (a, b) => a.schoolName?.localeCompare(b.schoolName),
    },
    {
      title: t('payment.coupon.column8'),
      dataIndex: 'gradeCode',
      key: 'gradeCode',
      align: 'center',
      sorter: (a, b) => a.gradeCode?.localeCompare(b.gradeCode),
      render: (value: string) =>
        getCommonCodeLabel('SY0006', String(value), 'ko'),
    },
    {
      title: t('payment.coupon.column9'),
      dataIndex: 'createSrtDate',
      key: 'createSrtDate',
      align: 'center',
      sorter: (a, b) => a.createSrtDate?.localeCompare(b.createSrtDate),
      render: (value: string) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('payment.coupon.column10'),
      dataIndex: 'createEndDate',
      key: 'createEndDate',
      align: 'center',
      sorter: (a, b) => a.createEndDate?.localeCompare(b.createEndDate),
      render: (value: string) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('payment.coupon.column11'),
      dataIndex: 'useYn',
      key: 'useYn',
      align: 'center',
      sorter: (a, b) => a.useYn?.localeCompare(b.useYn),
    },
  ];

  return (
    <div className="content scrollbar">
      {isLoading || isLoading2 ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <SimpleHeader parentMenu={t('common.menu.payment')} currentMenu={t('payment.coupon.title1')} />

      <div className='comm-table__area'>
        <Descriptions bordered column={3}>
          <Descriptions.Item label={t('payment.coupon.placeholder1')}>
            {campusCoupon?.couponName}
          </Descriptions.Item>
          <Descriptions.Item label={t('payment.coupon.column12')}>
            {campusCoupon?.amount.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label={t('payment.coupon.column13')}>
            {t('payment.coupon.text1')}{campusCoupon?.validDays}{t('payment.coupon.text2')}
          </Descriptions.Item>
          <Descriptions.Item label={t('payment.coupon.column2')}>
            {moment(campusCoupon?.createSrtDate).format('YYYY.MM.DD')}
          </Descriptions.Item>
          <Descriptions.Item label={t('payment.coupon.column3')} span={2}>
            {moment(campusCoupon?.createEndDate).format('YYYY.MM.DD')}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div className="comm-bx__between">
        <div className="comm-sch_result">{t('archive.exam.text1')}: <b>{data.length}</b>{t('common.text.unit')}</div>
        <div className="comm-btn__area">
          <Button
            className="btn-basic pink"
            onClick={() =>
              history.push(
                `/payment/coupon/${campusCode}/${couponCode}/issue`,
              )
            }
            text={t('payment.coupon.button1')}
          />
        </div>
      </div>
      <div className='comm-table__area'>
        <Table
          columns={columns}
          dataSource={data}
          pagination={{
            current: page,
            pageSize: config.pageSize,
          }}
        />
      </div>
      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => history.push('/payment/coupon')}
            text={t('common.button.back')}
          />
        </div>
        <div className="common-paging center">
          <Pagination
            count={data.length}
            page={page}
            param={{}}
            path={`/payment/coupon/${campusCode}/${couponCode}/list`}
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={() =>
              history.push(
                `/payment/coupon/${campusCode}/${couponCode}/issue`,
              )
            }
            text={t('payment.coupon.button1')}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PaymentCouponList;
